import type { MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';
import { Avatar as AvatarComponent } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { isStandalone } from '../../../constants';
import { useChannels } from '../../channels';
import { useButtonOpenLink, useSettingsFromFacebook } from '../utils';

interface AvatarProps {
  size?: number;
  alt?: string;
  showDot?: boolean;
  showBorder?: boolean;
}

export const Avatar = ({ size, alt, showDot, showBorder }: AvatarProps) => {
  const { headerBackgroundColor, customPicture, userOnlinePoint } =
    useSettings();
  const [channels] = useChannels();
  const linkSettings = isStandalone && {
    title: channels?.title,
    ...channels?.link
  };
  const opener = useButtonOpenLink(linkSettings || {});
  const { image } = useSettingsFromFacebook();

  const imageUrl = image || customPicture?.url;

  if (!imageUrl) {
    return null;
  }

  const openLink: MouseEventHandler = (e) => {
    e.preventDefault();

    opener?.();
  };

  return (
    <Component
      src={imageUrl}
      size={size}
      altText={alt}
      _borderSize={size}
      _showDot={!!showDot && !!imageUrl}
      _showBorder={!!showBorder}
      _borderColor={headerBackgroundColor}
      _onlinePoint={userOnlinePoint}
      _cursor={isStandalone}
      onClick={openLink}
    />
  );
};

export const Component = styled(AvatarComponent)<{
  _cursor: boolean;
  _onlinePoint: string;
  _showDot: boolean;
  _borderColor: string;
  _showBorder: boolean;
  _borderSize?: number;
}>`
  display: block;
  flex-shrink: 0;
  overflow: inherit;

  & img {
    object-fit: cover;
  }

  & svg {
    border-radius: inherit;
  }

  & > div {
    position: relative;
    z-index: 1;
  }

  &::before {
    content: '';
    bottom: 0;
    right: 0;
    width: 12px;
    height: 12px;
    box-sizing: border-box;
    position: absolute;
    z-index: 2;
    border-radius: 50%;
  }

  ${({ _cursor, _onlinePoint, _showDot, _borderColor }) => css`
    cursor: ${_cursor ? 'pointer' : 'auto'};

    &::before {
      background-color: ${_onlinePoint};
      display: ${_showDot ? 'block' : 'none'};
      border: 2px solid ${_borderColor};
    }
  `}

  ${({ _showBorder, _borderSize, _borderColor }) =>
    _showBorder &&
    css`
      &::after {
        content: '';
        position: absolute;
        width: ${_borderSize && _borderSize + 4}px;
        height: ${_borderSize && _borderSize + 4}px;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        border: double 2px transparent;
        border-radius: 50%;
        background-image:
          linear-gradient(${_borderColor}, ${_borderColor}),
          radial-gradient(circle at top right, #c82f9c, #ef4e49, #fecd68);
        background-origin: border-box;
        background-clip: padding-box, border-box;
      }
    `}
`;
